import { useTranslation } from 'react-i18next';
import type { Account, Id } from '@hero/schema';
import { getAccountBalanceForViewer, getAccountCounterparty } from '@hero/core';
import { Price } from '../../../app/Price';
import { AccountStatusBadge } from '../Status/AccountStatusBadge';

export interface AccountBalanceCardProps {
  /**
   * The bilateral Account doc whose balance we're rendering. When
   * `undefined`, the card shows a neutral "no movement yet" state —
   * used by the CRM Invoices tab while
   * `useAccountByPairApi` is still resolving or hasn't found
   * anything.
   */
  account: Account | null | undefined;
  /**
   * The viewer's party id (org id, typically). Drives the sign of
   * the displayed balance — positive = owed to you, negative = you
   * owe. Required to show the directional label; falls back to
   * `account.balance` raw value when omitted.
   */
  viewerOrgId?: Id | string;
  /**
   * Name to render as the counterparty header. When omitted, we
   * fall back to the counterparty's snapshot name on the account.
   * Hosts that have a richer Client / Org snapshot can override
   * here (e.g. CRM tab passes the CRM Client name).
   */
  counterpartyNameOverride?: string;
  className?: string;
}

/**
 * Counterparty-centric balance card. Mirrors
 * `WalletBalanceCard` (single-party) but is direction-aware via
 * `getAccountBalanceForViewer`.
 *
 * Three states:
 * - `account == null`            → neutral empty state.
 * - `viewerBalance == null`      → fallback to raw (shouldn't
 *                                  happen for party-scoped APIs;
 *                                  the backend 403s viewers off the
 *                                  account, but admins can hit
 *                                  this).
 * - viewer ∈ parties             → directional label + colour.
 */
export function AccountBalanceCard({
  account,
  viewerOrgId,
  counterpartyNameOverride,
  className,
}: AccountBalanceCardProps) {
  const { t } = useTranslation(['m-account']);

  if (!account) {
    return (
      <div
        className={`flex flex-col items-center justify-center w-full bg-white border rounded-lg py-10 ${
          className ?? ''
        }`}>
        <div className="flex flex-col items-center gap-1">
          <h2 className="h3 text-gray-400">
            <Price amount={0} />
          </h2>
          <h5 className="text-gray-500 body2">
            {t('m-account:balance_card.no_activity_yet', {
              defaultValue: 'No activity yet between you and this counterparty.',
            })}
          </h5>
        </div>
      </div>
    );
  }

  const viewerBalance = getAccountBalanceForViewer(account, viewerOrgId);
  const counterparty = getAccountCounterparty(account, viewerOrgId);
  const counterpartyName =
    counterpartyNameOverride ?? counterparty?.name ?? account.parties[1]?.name ?? '';

  const balance = viewerBalance ?? account.balance;
  const isPositive = balance > 0;
  const isZero = balance === 0;
  const labelKey = isZero
    ? 'balance_card.settled'
    : isPositive
    ? 'balance_card.owed_to_you'
    : 'balance_card.you_owe';
  const labelDefault = isZero
    ? 'Settled with {{name}}'
    : isPositive
      ? 'Owed to you'
      : 'You owe';
  const priceColor = isZero
    ? 'text-gray-700'
    : isPositive
    ? 'text-green-600'
    : 'text-red-600';

  return (
    <div
      className={`flex flex-col items-stretch w-full bg-white border rounded-lg p-6 gap-4 ${
        className ?? ''
      }`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col min-w-0">
          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {t('m-account:balance_card.counterparty', { defaultValue: 'Counterparty' })}
          </span>
          <span className="text-base font-semibold text-gray-900 truncate" title={counterpartyName}>
            {counterpartyName || '—'}
          </span>
        </div>
        <AccountStatusBadge status={account.status} />
      </div>

      <div className="flex flex-col items-start gap-1">
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {t(`m-account:${labelKey}`, {
            defaultValue: labelDefault,
            name: counterpartyName,
          })}
        </span>
        <h2 className={`h3 ${priceColor}`}>
          <Price amount={Math.abs(balance)} currency={account.currency} />
        </h2>
      </div>
    </div>
  );
}
